/**
 * Configuration Service
 * Handles API calls for system configuration management
 */
import { apiGet, apiPost, apiPut, apiDelete } from './api';

export interface ConfigCategory {
  categoryID: number;
  categoryName: string;
  categoryDescription: string;
  isActive: boolean;
  createdAt: string;
}

export interface ConfigValue {
  configValueID: number;
  categoryID: number;
  categoryName: string;
  configKey: string;
  configValue: string;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Get all configuration categories
 */
export async function getConfigCategories(): Promise<ConfigCategory[]> {
  const response = await apiGet<ConfigCategory[]>('/config/categories');

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch config categories error:', response.error);
    throw new Error(serverMsg || 'Unable to load configuration categories. Please try again.');
  }

  return response.data || [];
}

/**
 * Get configuration values by category name
 */
export async function getConfigByCategory(categoryName: string): Promise<ConfigValue[]> {
  const response = await apiGet<ConfigValue[]>(`/config/${categoryName}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    console.error('Fetch config by category error:', { categoryName, error: response.error });
    throw new Error(serverMsg || 'Unable to load configuration. Please try again.');
  }

  return response.data || [];
}

/**
 * Create a new configuration value
 */
export async function createConfigValue(data: {
  categoryID: number;
  configKey: string;
  configValue: string;
  displayOrder?: number;
}): Promise<ConfigValue> {
  const response = await apiPost<ConfigValue>('/config/values', data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to create configuration value. Please try again.');
  }

  return response.data;
}

/**
 * Update a configuration value
 */
export async function updateConfigValue(
  id: number,
  data: {
    configKey: string;
    configValue: string;
    displayOrder?: number;
  }
): Promise<ConfigValue> {
  const response = await apiPut<ConfigValue>(`/config/values/${id}`, data);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to update configuration value. Please try again.');
  }

  return response.data;
}

/**
 * Delete a configuration value
 */
export async function deleteConfigValue(id: number): Promise<void> {
  const response = await apiDelete<void>(`/config/values/${id}`);

  if (!response.success) {
    const serverMsg = response.error?.message || '';
    throw new Error(serverMsg || 'Unable to delete configuration value. Please try again.');
  }
}
